import { ConditionType } from '../../app/builder/contexts/PatternTreeContext'
import { isCompositeName } from './composite/refs'

function getOrdinalSuffix(num: number) {
  const j = num % 10,
    k = num % 100

  if (j === 1 && k !== 11) {
    return num + 'st'
  }
  if (j === 2 && k !== 12) {
    return num + 'nd'
  }
  if (j === 3 && k !== 13) {
    return num + 'rd'
  }
  return num + 'th'
}

function offsetToCandleString(offset: number) {
  if (offset <= 0) return 'on current candle'
  if (offset === 1) return 'on previous candle'
  return `on ${getOrdinalSuffix(offset)} previous candle`
}

// Returns { lhs, rhs } per-side strings for Exact mode, or a single trailing string for Some/All
function previousCandleInfo(condition: ConditionType): {
  perSide: boolean
  lhs: string
  rhs: string
  trailing: string
} {
  if (!condition.settings?.previousCandle?.enabled) {
    return { perSide: false, lhs: '', rhs: '', trailing: '' }
  }

  const numberOfCandles = condition.settings?.previousCandle?.numberOfCandles || 1
  const candlesNeeded = condition.settings?.previousCandle?.candlesNeeded || 0

  if (candlesNeeded === 0) {
    // Exact mode: both sides target the same candle
    return {
      perSide: true,
      lhs: offsetToCandleString(numberOfCandles),
      rhs: offsetToCandleString(numberOfCandles),
      trailing: '',
    }
  } else if (candlesNeeded === numberOfCandles) {
    return {
      perSide: false,
      lhs: '',
      rhs: '',
      trailing: `on all ${numberOfCandles} previous candles`,
    }
  }

  return {
    perSide: false,
    lhs: '',
    rhs: '',
    trailing: `on at least ${candlesNeeded} of the previous ${numberOfCandles} candles`,
  }
}

function candleTargetString(candle: any) {
  if (!candle) return 'on current candle'
  if (candle.lookback) {
    if (candle.lookback.mode === 'all') {
      return `on all ${candle.lookback.count} previous candles`
    }
    return `on at least ${candle.lookback.needed ?? 1} of the previous ${candle.lookback.count} candles`
  }
  if (candle.offset === 1) {
    return 'on the previous candle'
  }
  if (candle.offset > 1) {
    return `on the ${getOrdinalSuffix(candle.offset)} previous candle`
  }
  return 'on current candle'
}

function indicatorConditionString(condition: ConditionType) {
  const threshold = condition.condition.threshold

  const lhsField = condition.condition.LHS?.field
  const lhsStr = indicatorString(condition.condition.LHS)
  const rhsStr = indicatorString(condition.condition.RHS, lhsField)

  const hasThreshold = threshold !== undefined
  const thresholdStr = hasThreshold
    ? lhsField?.endsWith('Angle')
      ? ` ${threshold}° from`
      : ` ${threshold} from`
    : ''

  const prev = previousCandleInfo(condition)

  let result = lhsStr

  if (lhsStr) {
    if (prev.perSide) {
      result += ` ${prev.lhs}`
    } else if (!prev.trailing) {
      result += ` ${candleTargetString(condition.condition.LHS?.candle)}`
    }
  }

  if (lhsStr && condition.condition.conditionType) {
    result += condition.condition.conditionType.startsWith('changed') ? ' has' : ' is'
  }
  result += ` ${condition.condition.conditionType}`
  if (hasThreshold) result += thresholdStr
  if (rhsStr) result += ` ${rhsStr}`

  if (lhsStr && rhsStr) {
    if (prev.perSide) {
      result += ` ${prev.rhs}`
    } else if (!prev.trailing) {
      result += ` ${candleTargetString(condition.condition.RHS?.candle)}`
    }
  }

  if (prev.trailing) {
    result += ` ${prev.trailing}`
  }

  const validityCandles = condition.settings?.validityWindow?.candles
  if (validityCandles) {
    result += ` valid for ${validityCandles} candle${validityCandles !== 1 ? 's' : ''}`
  }

  return result
}

function indicatorString(indicator: any, lhsField?: string) {
  if (!indicator) return ''
  if (!indicator.indicator || !indicator.field) return ''

  if (indicator.value !== undefined) {
    return lhsField?.endsWith('Angle') ? `${indicator.value}°` : `${indicator.value}`
  }

  // Custom composites: the raw name is an opaque id — label from the
  // condition's embedded snapshot instead
  if (isCompositeName(indicator.indicator.name)) {
    const snapshotName = indicator.indicator.params?.snapshot?.name
    const base = typeof snapshotName === 'string' ? snapshotName : 'Custom Indicator'
    return indicator.field && indicator.field !== 'value'
      ? `${base} ${reverseCamelCase(indicator.field)}`
      : base
  }

  if (indicator.indicator.name === indicator.field) {
    return reverseCamelCase(indicator.indicator.name)
  }

  const nameStr = reverseCamelCase(indicator.indicator.name)
  const fieldStr = reverseCamelCase(indicator.field)

  // Check if the field contains most of the indicator name's words
  const nameWords = nameStr.toLowerCase().split(' ')
  const fieldLower = fieldStr.toLowerCase()
  const matchCount = nameWords.filter((w: string) => fieldLower.includes(w)).length

  if (matchCount / nameWords.length >= 0.5) {
    // Field is descriptive enough on its own (e.g. "Simple Moving Average Angle")
    return fieldStr
  }

  // Field is distinct — show field then indicator name
  return `${fieldStr} ${nameStr}`
}

function reverseCamelCase(str: string) {
  if (!str) return str
  const spaced = str.replace(/([A-Z])/g, ' $1')
  const capitalized = spaced.charAt(0).toUpperCase() + spaced.slice(1)
  return capitalized
}

export default indicatorConditionString
